import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PiTray } from 'react-icons/pi';
import { Card, Skeleton, Paginator, Button } from '@/components/ui';
import { StatusBadge } from '@/components/worker/StatusBadge/StatusBadge';
import { BalanceChip } from '@/components/worker/BalanceChip/BalanceChip';
import { RewardDisplay } from '@/components/job/RewardDisplay/RewardDisplay';
import { useAssignments, useWorkerProfile } from '@/api/worker';
import { ROUTES } from '@/constants/routes';
import styles from './AssignmentsPage.module.css';

const TABS = [
    { key: 'assigned', label: 'En Progreso' },
    { key: 'submitted', label: 'Enviados' },
    { key: 'approved', label: 'Completados' },
    { key: 'rejected', label: 'Rechazados' },
];

const PAGE_SIZE = 10;

export function AssignmentsPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('assigned');
    const [page, setPage] = useState(1);

    const { data: profile } = useWorkerProfile();
    const { data, isLoading } = useAssignments({
        status: activeTab,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
    });

    const assignments = data?.results || [];
    const totalPages = Math.ceil((data?.count || 0) / PAGE_SIZE);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Mis Trabajos</h1>
                {profile && <BalanceChip amount={profile.balance} />}
            </div>

            <div className={styles.tabBar}>
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                        onClick={() => { setActiveTab(tab.key); setPage(1); }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <div className={styles.list}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Card key={i} className={styles.card}>
                            <div className={styles.cardHeader}>
                                <Skeleton height={18} width="50%" />
                                <Skeleton width={90} height={24} variant="rounded" />
                            </div>
                            <div className={styles.cardFooter}>
                                <Skeleton width={100} height={22} variant="rounded" />
                                <Skeleton width={80} height={14} />
                            </div>
                        </Card>
                    ))}
                </div>
            ) : assignments.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyIconWrapper}>
                        <PiTray className={styles.emptyIcon} />
                    </div>
                    <h3 className={styles.emptyTitle}>No tienes trabajos en esta categoria</h3>
                    <p className={styles.emptyDescription}>Explora el feed para encontrar trabajos disponibles.</p>
                    <Button variant="primary" onClick={() => navigate(ROUTES.home)}>
                        Ver trabajos
                    </Button>
                </div>
            ) : (
                <>
                    <div className={styles.list}>
                        {assignments.map((a) => (
                            <Card
                                key={a.uuid}
                                className={styles.card}
                                hoverable
                                onClick={() => navigate(`/assignments/${a.uuid}`)}
                            >
                                <div className={styles.cardHeader}>
                                    <h3 className={styles.cardTitle}>{a.job_title}</h3>
                                    <StatusBadge status={a.status} />
                                </div>
                                <div className={styles.cardFooter}>
                                    <RewardDisplay amount={a.job_reward || '0'} size="sm" />
                                    <span className={styles.date}>
                                        {new Date(a.created).toLocaleDateString('es-PE')}
                                    </span>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className={styles.pagination}>
                            <Paginator page={page} numPages={totalPages} onPageChange={setPage} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
